import { useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { Button } from "@src/components/Button";
import { Icon } from "@src/components/Icon";

type ForbiddenContentProps = {
  /**
   * Optional title to display above the description.
   * Defaults to "Access Denied".
   */
  title?: string;
  /**
   * Optional primary message shown under the title.
   */
  message?: string;
  /**
   * Optional secondary message shown under the primary message.
   */
  secondaryMessage?: string;
  /**
   * Whether to render navigation buttons.
   * Useful to disable when rendering inside a tab/screen that already
   * has its own navigation affordances.
   *
   * Defaults to true.
   */
  showActions?: boolean;
};

export function ForbiddenContent({
  title = "Access Denied",
  message = "You don't have permission to access this resource.",
  secondaryMessage = "Please contact your administrator if you believe this is an error.",
  showActions = true,
}: ForbiddenContentProps) {
  const router = useRouter();
  const { theme } = useUnistyles();

  return (
    <View flex={1} justifyContent="center" alignItems="center" p="6xl" gap="4xl">
      <Icon name="lockClosed" size={64} color={theme.colors.danger[500]} />

      <View gap="md" alignItems="center">
        <Text textStyle="2xl" fontWeight="bold" textAlign="center">
          {title}
        </Text>
        <Text textStyle="md/regular" color="textMuted" textAlign="center">
          {message}
        </Text>
        <Text textStyle="sm/regular" color="textMuted" textAlign="center">
          {secondaryMessage}
        </Text>
      </View>

      {showActions ?
        <View gap="md" style={{ width: "100%" }}>
          <Button
            label="Go to Home"
            size="md"
            variant="primary"
            variantColor="blue"
            onPress={() => router.replace("/")}
          />
          <Button label="Go Back" size="md" variant="secondary" variantColor="blue" onPress={() => router.back()} />
        </View>
      : null}
    </View>
  );
}
